import { FC, PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CardOnboardingStepState } from '../../../../queries/pxtraCard';
import { CheckmarkCircleFilledIcon } from '../../../icons';

type Props = {
  state: CardOnboardingStepState;
  title: string;
  description: ReactNode;
  action?: ReactNode;
  /** Overrides the chip where "on you" would name the wrong person. */
  stateLabel?: string;
};

const LABEL = {
  todo: 'pxtraCard:stepStateTodo',
  waiting: 'pxtraCard:stepStateWaiting'
} as const;

const LABEL_CLASS = {
  todo: 'bg-warning-lightest text-warning-dark',
  waiting: 'bg-info-lightest text-info-dark'
} as const;

// box-border: preflight is off, so without it the 2px outline of an upcoming
// step is added outside the 16px box and the circle reads bigger than the
// filled dots.
const MARKER_CLASS = {
  upcoming: 'border-2 border-solid border-grey-200',
  todo: 'bg-warning-main',
  waiting: 'bg-info-main',
  done: ''
} as const;

/**
 * A done step is marked by the checkmark alone — a coloured dot beside it
 * would say the same thing twice.
 */
const Marker: FC<{ state: CardOnboardingStepState }> = ({ state }) => (
  // h-6 matches the title's line box, so the marker centres on the title
  // rather than floating above it. shrink-0 keeps the dot a circle: inside a
  // flex row it would otherwise be squeezed narrower than it is tall.
  <span className="flex h-6 w-4 shrink-0 items-center justify-center">
    {state === 'done' ? (
      <CheckmarkCircleFilledIcon className="h-4 w-4 shrink-0 text-success-main" />
    ) : (
      <span className={`box-border block h-4 w-4 shrink-0 rounded-full ${MARKER_CLASS[state]}`} />
    )}
  </span>
);

export const ChecklistItem: FC<PropsWithChildren<Props>> = ({
  state,
  title,
  description,
  action,
  stateLabel,
  children
}) => {
  const { t } = useTranslation();
  const key = state === 'todo' || state === 'waiting' ? LABEL[state] : null;
  const label = stateLabel ?? (key ? t(key) : null);

  return (
    <div
      className={`rounded-xl border border-solid border-grey-100 p-4 ${
        state === 'upcoming' ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 gap-3">
          <Marker state={state} />

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="m-0 font-medium text-midnight-main">{title}</p>
              {label && (
                <span
                  className={`rounded-md px-2 py-0.5 text-xs font-medium ${
                    LABEL_CLASS[state as 'todo' | 'waiting']
                  }`}
                >
                  {label}
                </span>
              )}
            </div>

            <p className="m-0 mt-1 text-sm text-grey-600">{description}</p>

            {children}
          </div>
        </div>

        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
};
